<script setup lang="ts">
  /**
   * Plan card — 03-components/20d-plan-card · OSCloud DS 1.1.166
   *
   * The selectable option at card rank, BfSelectionCard's sibling. The hidden radio is the state
   * store: hover, selected, focus and disabled are all read off it, never a modifier. Unlike the
   * row-rank card the input does not wrap the whole card, so the body can hold any DS block; a
   * click anywhere on the card that is not itself interactive picks it, and the footer "Choose"
   * button picks it too, mirroring the input through aria-pressed.
   */
  import { computed, useId } from 'vue'
  import BfButton from './BfButton.vue'
  import BfIconCheck from '@/components/ui/icons/BfIconCheck.vue'

  type PlanValue = string | number

  const props = withDefaults(
    defineProps<{
      modelValue?: PlanValue | null
      value: PlanValue
      /** Shared input name for the group. */
      name?: string
      title: string
      sub?: string
      /** The price element: currency + amount + period on one baseline. Omit `amount` for none. */
      currency?: string
      amount?: string
      period?: string
      /** Footer button text while unchosen. Defaults to "Choose <title>". */
      chooseLabel?: string
      /** Heading level of the title, following the page outline. */
      titleLevel?: 2 | 3 | 4
      disabled?: boolean
      /** The visible radio dot (DS __control) before the title — for a compact card with no footer. */
      control?: boolean
      /** The footer "Choose" button. Off for a compact card whose whole surface is the target. */
      chooseButton?: boolean
    }>(),
    {
      modelValue: null,
      name: undefined,
      sub: '',
      currency: '',
      amount: '',
      period: '',
      chooseLabel: '',
      titleLevel: 3,
      disabled: false,
      control: false,
      chooseButton: true,
    },
  )

  const emit = defineEmits<{ 'update:modelValue': [value: PlanValue] }>()

  const inputId = useId()
  const isChecked = computed(() => props.modelValue === props.value)

  const select = () => {
    if (!props.disabled) emit('update:modelValue', props.value)
  }

  // The footer button and the head label are targets of their own; any other interactive
  // element in the body (a link, a field) keeps its click.
  const INTERACTIVE = 'a, button, input, select, textarea, label, [role="button"]'

  const onCardClick = (event: MouseEvent) => {
    if ((event.target as Element).closest(INTERACTIVE)) return
    select()
  }
</script>

<template>
  <div class="plan-card" @click="onCardClick">
    <input
      :id="inputId"
      type="radio"
      :name="name"
      :value="value"
      :checked="isChecked"
      :disabled="disabled"
      @change="select"
    />
    <span v-if="$slots.flag" class="plan-card__flag"><slot name="flag" /></span>
    <label class="plan-card__head" :for="inputId">
      <span v-if="control" class="plan-card__control"><span class="radio__dot" aria-hidden="true" /></span>
      <span class="plan-card__text">
        <span class="plan-card__title" role="heading" :aria-level="titleLevel">{{ title }}</span>
        <span v-if="sub" class="plan-card__sub">{{ sub }}</span>
      </span>
    </label>
    <div v-if="amount || $slots.default" class="plan-card__body">
      <div v-if="amount" class="plan-card__price">
        <span v-if="currency" class="plan-card__price-currency">{{ currency }}</span>
        <span class="plan-card__price-amount">{{ amount }}</span>
        <span v-if="period" class="plan-card__price-period">{{ period }}</span>
      </div>
      <slot />
    </div>
    <div v-if="chooseButton" class="plan-card__footer">
      <BfButton variant="outline" full-width :pressed="isChecked" :disabled="disabled" @click="select">
        <BfIconCheck v-if="isChecked" class="plan-card__choose-mark" />
        {{ isChecked ? 'Selected' : chooseLabel || `Choose ${title}` }}
      </BfButton>
    </div>
  </div>
</template>

<style scoped lang="scss">
  .plan-card {
    --plan-card-padding: 22px;

    position: relative;
    display: flex;
    flex-direction: column;
    gap: var(--bf-sp-3);
    padding: var(--plan-card-padding);
    background: var(--bf-white);
    border: 1px solid var(--bf-line);
    border-radius: var(--bf-r-lg);
    cursor: pointer;
    transition:
      border-color var(--bf-dur-fast) var(--bf-ease),
      box-shadow var(--bf-dur-fast) var(--bf-ease),
      transform var(--bf-dur-fast) var(--bf-ease);

    > input {
      position: absolute;
      opacity: 0;
      pointer-events: none;
    }
  }

  /* At card rank the hover affordance is a lift. Only an unchosen, enabled card lifts: the
     chosen one is the answer and sits still under the cursor. */
  .plan-card:hover:not(:has(> input:checked)):not(:has(> input:disabled)) {
    transform: translateY(-3px);
    box-shadow: var(--bf-shadow-2);
  }

  /* Selected: the doubled orange edge and a resting shadow in the accent's hue. No fill — the
     surface is the option's own content, and tinting it recolours what is being compared. */
  .plan-card:has(> input:checked) {
    border-color: var(--bf-orange-dark);
    box-shadow:
      inset 0 0 0 1px var(--bf-orange-dark),
      var(--bf-shadow-selected);
  }

  .plan-card:has(> input:focus-visible) {
    box-shadow: var(--bf-form-focus-ring);
  }

  .plan-card:has(> input:checked):has(> input:focus-visible) {
    box-shadow:
      inset 0 0 0 1px var(--bf-orange-dark),
      var(--bf-shadow-selected),
      var(--bf-form-focus-ring);
  }

  /* --flat lives on the group: the choice belongs to the picker's surroundings. */
  .plan-card-group--flat .plan-card:has(> input:checked) {
    box-shadow: inset 0 0 0 1px var(--bf-orange-dark);
  }

  .plan-card-group--flat .plan-card:has(> input:checked):has(> input:focus-visible) {
    box-shadow:
      inset 0 0 0 1px var(--bf-orange-dark),
      var(--bf-form-focus-ring);
  }

  .plan-card:has(> input:disabled) {
    opacity: var(--bf-form-disabled-opacity);
  }

  .plan-card:has(> input:disabled),
  .plan-card:has(> input:disabled) .plan-card__head {
    cursor: not-allowed;
  }

  /* One badge on the top edge, inset by the padding. The card reserves no space for it. */
  .plan-card__flag {
    position: absolute;
    top: 0;
    left: var(--plan-card-padding);
    transform: translateY(-50%);
    display: flex;
  }

  .plan-card__head {
    display: flex;
    align-items: flex-start;
    gap: var(--bf-sp-2);
    cursor: pointer;
    user-select: none;
  }

  .plan-card__control {
    flex: 0 0 auto;
    display: flex;
    margin-top: 3px;
  }

  /* The dot is copied from Radio (20a) and never restyled; only its checked selector is rewritten
     for this block, where the input is a sibling of __head rather than of the dot. */
  .radio__dot {
    flex-shrink: 0;
    width: 16px;
    height: 16px;
    margin-top: 1px;
    border: 1.5px solid var(--bf-dusky-2);
    border-radius: 50%;
    background: var(--bf-form-control-surface);
    transition:
      border-color var(--bf-dur-fast) var(--bf-ease),
      border-width var(--bf-dur-fast) var(--bf-ease);
  }

  .plan-card > input:checked ~ .plan-card__head .radio__dot {
    border-width: 5px;
    border-color: var(--bf-orange-dark);
  }

  .plan-card__text {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
  }

  .plan-card__title {
    margin: 0;
    font-family: var(--bf-font-display);
    font-size: var(--bf-fs-md);
    font-weight: var(--bf-fw-semibold);
    line-height: var(--bf-lh-tight);
    letter-spacing: var(--bf-tr-tight);
    color: var(--bf-fg-1);
    overflow-wrap: anywhere;
  }

  .plan-card__sub {
    /* The DS body face, restated: the app's <body> is still the legacy Work Sans. */
    font-family: var(--bf-font-body);
    font-size: var(--bf-fs-xs_5);
    color: var(--bf-fg-2);
  }

  /* A stack of DS blocks; nothing here styles them. */
  .plan-card__body {
    display: flex;
    flex-direction: column;
    gap: var(--bf-sp-3);
    min-width: 0;
  }

  /* Price: an element of this block. The amount is the type scale's stat value, the currency
     follows the order summary's total, so the two read as the same kind of number. */
  .plan-card__price {
    display: flex;
    align-items: baseline;
    gap: 5px;
  }

  .plan-card__price-currency {
    font-family: var(--bf-font-display);
    font-size: var(--bf-fs-sm);
    font-weight: var(--bf-fw-semibold);
    letter-spacing: var(--bf-tr-wide);
    color: var(--bf-fg-2);
  }

  .plan-card__price-amount {
    font-family: var(--bf-font-display);
    font-size: var(--bf-fs-2xl);
    font-weight: var(--bf-fw-extrabold);
    line-height: 1;
    letter-spacing: var(--bf-tr-tight);
    font-variant-numeric: tabular-nums;
    color: var(--bf-fg-1);
  }

  .plan-card__price-period {
    font-size: var(--bf-fs-xs_5);
    color: var(--bf-fg-2);
  }

  /* Pinned to the bottom so the buttons line up across cards of different body height. */
  .plan-card__footer {
    margin-top: auto;
    display: flex;
    flex-direction: column;
    gap: var(--bf-sp-2);
  }

  .plan-card__choose-mark {
    flex: 0 0 auto;
  }

  /* The chosen card promotes its pressed outline button to the primary recipe (06): one filled
     button in the group, on the card that owns the choice. The markup stays outline + pressed. */
  .plan-card:has(> input:checked) .plan-card__footer :deep(.button--outline[aria-pressed='true']) {
    background: linear-gradient(to bottom, var(--bf-orange-hover), hsl(var(--dk-primary)));
    color: hsl(var(--dk-primary-foreground));
    border-color: transparent;
    box-shadow: 0 1px 2px rgba(234, 96, 10, 0.25);
  }

  .plan-card:has(> input:checked) .plan-card__footer :deep(.button--outline[aria-pressed='true']:hover:not(:disabled)) {
    box-shadow: 0 2px 10px rgba(234, 96, 10, 0.35);
    filter: brightness(1.05);
  }
</style>
